import SmartLeadImage from "./SmartLeadImage";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import Reveal from "./Reveal";
import { titleCaseHeadline } from "../utils/titleCase";

export const CategorySection = ({ section, index }) => (
  <section
    id={section.id}
    data-testid={`category-section-${section.id}`}
    className={section.id === "sports" ? "sports-feature-section" : section.id === "opinions" ? "opinions-feature-section" : "bg-[var(--bg)]"}
  >
    {section.id === "sports" && <div className="sports-football-art" aria-hidden="true"><img src="/sports-football.svg" alt="" width="640" height="640" /></div>}
    {section.id === "opinions" && <div className="opinions-pen-art" aria-hidden="true"><img src="/opinions-pen.svg" alt="" width="640" height="640" /></div>}
    <div className={`mx-auto max-w-[1440px] px-4 sm:px-6 ${section.id === "sports" ? "sports-feature-content" : section.id === "opinions" ? "opinions-feature-content" : "pb-14 sm:pb-16"}`}>
      <Reveal>
        <div className={`flex flex-wrap items-center justify-between gap-4 rounded-[4px] overflow-hidden border-t-[3px] border-[#cf0a0a] bg-[var(--bg-soft)] px-5 sm:px-7 py-5 ${section.id === "sports" ? "sports-feature-heading" : section.id === "opinions" ? "opinions-feature-heading" : ""}`}>
          <h2 className="font-display text-3xl font-semibold">{section.kicker}</h2>
          <a
            href={`/category/${section.id}`}
            data-testid={`category-section-more-${section.id}`}
            className="flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-[0.2em] text-[var(--accent)] hover:gap-2 transition-all duration-300"
          >
            All {section.kicker.toLowerCase()} <ArrowUpRight size={13} />
          </a>
        </div>
      </Reveal>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-7 sm:gap-8 lg:gap-10 mt-6 sm:mt-8">
        {/* Lead */}
        <Reveal className="lg:col-span-5" delay={0.05}>
          <Link
            to={`/${section.lead.id}`}
            data-testid={`section-lead-${section.id}`}
            className="block group"
          >
            <div className="img-frame overflow-hidden aspect-[16/9] bg-[var(--card-muted)]">
              <SmartLeadImage story={section.lead} />
            </div>
            <p className="mt-4 flex items-center justify-between gap-3">
              <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[var(--accent)]">
                {section.lead.kicker || section.kicker}
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-[var(--faint)] whitespace-nowrap">
                <Clock size={11} /> {section.lead.date}
              </span>
            </p>
            <h3 className="title-black mt-1.5 font-display text-2xl sm:text-[1.7rem] leading-snug line-clamp-2 font-semibold group-hover:text-[var(--accent)] transition-colors duration-300">
              {titleCaseHeadline(section.lead.title)}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-[var(--muted)] line-clamp-2">
              {section.lead.excerpt}
            </p>
          </Link>
        </Reveal>

        {/* Stack */}
        <div className="lg:col-span-4 lg:border-l lg:border-[var(--line)] lg:pl-8">
          <ul className="divide-y divide-[var(--line)]">
            {section.stack.map((item, i) => (
              <li key={item.id}>
                <Reveal delay={0.1 + i * 0.06}>
                  <Link
                    to={`/${item.id}`}
                    data-testid={`section-stack-${section.id}-${i + 1}`}
                    className="flex items-start gap-3 py-3.5 group/stack"
                  >
                      <div className="img-frame w-[88px] h-[64px] shrink-0 overflow-hidden bg-[var(--card-muted)]"><img src={item.img} alt={item.imgAlt} loading="lazy" className="h-full w-full object-cover" /></div>
                      <div className="min-w-0 flex-1">
                    <span className="block font-display text-lg leading-snug line-clamp-2 font-medium transition-colors duration-300 group-hover/stack:text-[var(--accent)]">
                      {titleCaseHeadline(item.title)}
                    </span>
                    <span className="mt-1 block text-[10px] text-[var(--faint)]">
                      {item.date}
                    </span>
                  
                      </div>
                    </Link>
                </Reveal>
              </li>
            ))}
          </ul>
        </div>

        {/* More panel */}
        <div className="lg:col-span-3">
          <Reveal delay={0.15} className="h-full">
            <div className={`h-full border border-[var(--line)] bg-[var(--bg-soft)] p-5 flex flex-col ${section.id === "sports" ? "sports-feature-more" : section.id === "opinions" ? "opinions-feature-more" : ""}`}>
              <p className="text-[10px] font-extrabold uppercase tracking-[0.22em] text-[var(--faint)]">
                More from {section.kicker}
              </p>
              <ul className="mt-2 divide-y divide-[var(--line)] flex-1">
                {section.links.map((item, i) => (
                  <li key={item.id}>
                    <Link
                      to={`/${item.id}`}
                      data-testid={`section-link-${section.id}-${i + 1}`}
                      className="flex items-start gap-3 py-3 group/link"
                    >
                      <div className="img-frame w-[64px] h-[48px] shrink-0 overflow-hidden bg-[var(--card-muted)]"><img src={item.img} alt={item.imgAlt} loading="lazy" className="h-full w-full object-cover" /></div>
                      <div className="min-w-0 flex-1">
                      <span className="block text-[13px] leading-snug line-clamp-2 font-medium transition-colors duration-300 group-hover/link:text-[var(--accent)]">
                        {titleCaseHeadline(item.title)}
                      </span>
                      <span className="mt-0.5 block text-[10px] text-[var(--faint)]">
                        {item.date}
                      </span>
                    
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

export default CategorySection;
